import { useInfiniteQuery } from 'react-query';
import { trackCallHistoryViewed } from '../../analytics';

import moment from 'moment';
import { lovageApi } from '../../axios';
import { snakeify } from '../../utils/formatObjKeys';
import { fDate } from '../../utils/formatTime';
const qs = require('qs');

export const callHistoryFilterOptions = [
  'connected_to_shop',
  'links_clicked',
  'texts_sent',
  'question_answered',
];
export type CallHistoryFilterOption = typeof callHistoryFilterOptions[number];

export interface CallHistory {
  items: CallLog[];
  total: number;
  limit: number;
  offset: number;
}

export interface CallLog {
  direction: 'inbound' | 'outbound';
  duration: number | null;
  location_name: string;
  from_formatted: string;
  end_time: string | null; //date
  connected_to_shop: boolean;
  total_messages_sent: number;
  total_shortlink_visits: number;
  total_questions_answered: number;
}

export interface CallHistoryRequest {
  locationIds: number[] | undefined;
  limit: number;
  startDate: Date;
  endDate: Date;
  callHistoryFilter: CallHistoryFilterOption[];
  offset: number;
  timezone: string;
}

/**
 * Extra metadata used if recording analytics for the call history view is desired
 */
export interface Analytics {
  timeframe: string; // timeframe label
}

interface CallHistoryArgs {
  request: Omit<CallHistoryRequest, 'offset'>;
  analytics?: Analytics;
}

const validRequest = (request: CallHistoryArgs['request']): boolean =>
  (request.locationIds && request.locationIds.length > 0) ?? false;

export const fetchCallHistory = (
  request: CallHistoryRequest,
  analytics: CallHistoryArgs['analytics']
) => {
  console.log('fetchCallHistory');

  if (analytics && validRequest(request) && request.locationIds) {
    const totalDays = moment(request.endDate).diff(request.startDate, 'days');
    trackCallHistoryViewed({
      locationIds: request.locationIds,
      filters: request.callHistoryFilter,
      startDate: request.startDate,
      endDate: request.endDate,
      timeframe: analytics.timeframe,
      totalDays,
      offset: request.offset,
    });
  }

  if (!validRequest(request)) {
    console.warn('No locationId provided - fetch aborted.');
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { location_ids, ...snakeifiedRequest } = snakeify(request);

  // Change locationIds to location_id
  let finalRequest = {
    ...snakeifiedRequest,
    location_id: request.locationIds,
  };

  return lovageApi
    .get<CallHistory>(`/dashboard/call-history`, {
      params: finalRequest,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    })
    .then((res) => res.data);
};

export default function useCallHistory({ request, analytics }: CallHistoryArgs) {
  const { locationIds, callHistoryFilter, startDate, endDate } = request;

  const queryKey = [
    'callHistory',
    locationIds,
    callHistoryFilter,
    { startDate: fDate(startDate), endDate: fDate(endDate) }, // fDate gets just the month, day, and year so React Query doesn't invalidate the cache for identical dates
  ];

  return useInfiniteQuery(
    queryKey,
    ({ pageParam = 0 }) =>
      fetchCallHistory(
        {
          ...request,
          offset: pageParam,
        },
        analytics
      ),
    {
      getNextPageParam: (lastPageData, _pages) => {
        const lastPageItems = lastPageData?.items;

        if (!lastPageItems) {
          return undefined;
        }

        if (lastPageItems.length === 0) {
          return undefined;
        }

        const { total, offset, limit } = lastPageData;
        const nextOffset = offset + limit;

        if (offset + lastPageItems.length < total) {
          return nextOffset;
        }

        return undefined;
      },
      refetchOnWindowFocus: process.env.NODE_ENV === 'production',
    }
  );
}
